import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { resolveBackendUniversityId } from '../config/cities';
import { formatApiErrorDetail } from '../utils/apiError';


interface User {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
    university?: string;
    university_id?: number;
    university_abbreviation?: string;
    created_at: string;
    email_verification_status: 'unverified' | 'verified';
    is_google_user: boolean;
    is_microsoft_user?: boolean;
    avatar_url?: string; // Keep this for future avatar support
    instagram?: string;
    preferred_university_id?: number;
    preferred_term?: string;
    // Backward compatibility
    is_verified: boolean;
    has_listings: boolean;
    student_verification_status?: 'unverified' | 'pending' | 'approved' | 'rejected';
    student_id_url?: string;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    // Verification status helpers
    isEmailVerified: boolean;
    login: (email: string, password: string) => Promise<void>;
    loginWithGoogle: (googleToken: string) => Promise<{ requiresSetup?: boolean; setupData?: any }>;
    loginWithMicrosoft: (microsoftToken: string) => Promise<{ requiresSetup?: boolean; setupData?: any }>;
    register: (email: string, password: string, firstName: string, lastName: string, phone?: string, universityId?: string, instagram?: string, university?: string) => Promise<{ success: boolean; message: string }>;
    registerWithGoogle: (googleToken: string) => Promise<{ requiresSetup?: boolean; setupData?: any }>;
    registerWithMicrosoft: (microsoftToken: string) => Promise<{ requiresSetup?: boolean; setupData?: any }>;
    logout: () => void;
    updateProfile: (updates: Partial<User>) => Promise<void>;
    refreshUserProfile: () => Promise<void>;
    updatePreferences: (preferences: { preferred_university_id?: number; preferred_term?: string }) => Promise<void>;
    loadUserPreferences: () => Promise<void>;
    handleEmailVerificationSuccess: (userData: any, token: string) => Promise<void>;
    refreshToken: () => Promise<boolean>;
    makeAuthenticatedRequest: (url: string, options?: RequestInit) => Promise<Response>;
    showGoogleSetupModal: (setupData: any) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(() => {
        const savedUser = localStorage.getItem('user_data');
        if (savedUser) {
            try {
                return JSON.parse(savedUser);
            } catch (e) {
                return null;
            }
        }
        return null;
    });
    const [token, setToken] = useState<string | null>(() => localStorage.getItem('auth_token'));
    const [isLoading, setIsLoading] = useState(() => {
        const savedToken = localStorage.getItem('auth_token');
        const savedUser = localStorage.getItem('user_data');
        return !(savedToken && savedUser);
    });

    const isAuthenticated = !!user && !!token;
    const isEmailVerified = user?.email_verification_status === 'verified' || user?.is_verified || false;

    const logout = (): void => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
    };

    const refreshToken = async (): Promise<boolean> => {
        const currentToken = localStorage.getItem('auth_token');
        if (!currentToken) return false;

        try {
            const response = await fetch(`${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${currentToken}`
                },
                body: JSON.stringify(currentToken)
            });

            if (response.ok) {
                const data = await response.json();
                setToken(data.access_token);
                setUser(data.user);
                localStorage.setItem('auth_token', data.access_token);
                localStorage.setItem('user_data', JSON.stringify(data.user));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Token refresh failed:', error);
            return false;
        }
    };

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const queryParams = new URLSearchParams(window.location.search);
                let impersonationToken = queryParams.get('impersonation_token');

                if (!impersonationToken && window.location.hash.includes('impersonation_token')) {
                    const hashPart = window.location.hash.includes('?') 
                        ? window.location.hash.split('?')[1] 
                        : window.location.hash.replace('#', '').replace(/^\//, '');
                    const hashParams = new URLSearchParams(hashPart);
                    impersonationToken = hashParams.get('impersonation_token');
                }

                if (impersonationToken) {
                    localStorage.setItem('auth_token', impersonationToken);
                    localStorage.removeItem('user_data');
                }

                const savedToken = localStorage.getItem('auth_token');
                const savedUser = localStorage.getItem('user_data');

                if (savedToken) {
                    if (savedUser) {
                        setIsLoading(false);
                    }
                    try {
                        const response = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                            headers: {
                                'Authorization': `Bearer ${savedToken}`,
                                'Content-Type': 'application/json'
                            }
                        });

                        if (response.ok) {
                            const freshUserData = await response.json();
                            console.log('Auth initialization: Profile loaded', { 
                                id: freshUserData.id, 
                                email: freshUserData.email, 
                                has_listings: freshUserData.has_listings 
                            });
                            setToken(savedToken);
                            setUser(freshUserData);
                            localStorage.setItem('user_data', JSON.stringify(freshUserData));

                            if (impersonationToken) {
                                const url = new URL(window.location.href);
                                url.searchParams.delete('impersonation_token');
                                window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
                            }
                        } else if (response.status === 401) {
                            console.log('Auth initialization: Token expired, attempting refresh...');
                            const refreshed = await refreshToken();
                            if (!refreshed) {
                                logout();
                            }
                        }
                    } catch (error) {
                        console.error('Initial auth check failed:', error);
                        if (savedUser) {
                            try {
                                const parsedUser = JSON.parse(savedUser);
                                setUser(parsedUser);
                                setToken(savedToken);
                            } catch (e) {
                                logout();
                            }
                        }
                    }
                }
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, []);

    const login = async (email: string, password: string): Promise<void> => {
        try {
            setIsLoading(true);
            const response = await fetch(API_ENDPOINTS.AUTH.LOGIN, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(formatApiErrorDetail(error.detail, 'Login failed'));
            }

            const data = await response.json();
            setToken(data.access_token);
            setUser(data.user);

            localStorage.setItem('auth_token', data.access_token);
            localStorage.setItem('user_data', JSON.stringify(data.user));

            // Refresh user profile to get complete data including avatar
            try {
                const profileResponse = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                    headers: {
                        'Authorization': `Bearer ${data.access_token}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (profileResponse.ok) {
                    const freshUserData = await profileResponse.json();
                    setUser(freshUserData);
                    localStorage.setItem('user_data', JSON.stringify(freshUserData));
                }
            } catch (profileError) {
                console.warn('Failed to refresh profile after login:', profileError);
            }
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const loginWithGoogle = async (googleToken: string): Promise<{ requiresSetup?: boolean; setupData?: any }> => {
        try {
            setIsLoading(true);
            const response = await fetch(API_ENDPOINTS.AUTH.GOOGLE_LOGIN, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: googleToken }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(formatApiErrorDetail(error.detail, 'Google login failed'));
            }

            const data = await response.json();

            // Check if account setup is required
            if (data.requires_setup) {
                return {
                    requiresSetup: true,
                    setupData: {
                        user_id: data.user_id,
                        email: data.email,
                        first_name: data.first_name,
                        last_name: data.last_name
                    }
                };
            }

            // Account is complete, proceed with normal login
            setToken(data.access_token);
            setUser(data.user);

            localStorage.setItem('auth_token', data.access_token);
            localStorage.setItem('user_data', JSON.stringify(data.user));

            // Refresh user profile to get complete data including avatar
            try {
                const profileResponse = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                    headers: {
                        'Authorization': `Bearer ${data.access_token}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (profileResponse.ok) {
                    const freshUserData = await profileResponse.json();
                    setUser(freshUserData);
                    localStorage.setItem('user_data', JSON.stringify(freshUserData));
                }
            } catch (profileError) {
                console.warn('Failed to refresh profile after login:', profileError);
            }

            return { requiresSetup: false };
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const loginWithMicrosoft = async (microsoftToken: string): Promise<{ requiresSetup?: boolean; setupData?: any }> => {
        try {
            setIsLoading(true);
            const response = await fetch(API_ENDPOINTS.AUTH.MICROSOFT_LOGIN, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: microsoftToken }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(formatApiErrorDetail(error.detail, 'Microsoft login failed'));
            }

            const data = await response.json();

            // Check if account setup is required
            if (data.requires_setup) {
                return {
                    requiresSetup: true,
                    setupData: {
                        user_id: data.user_id,
                        email: data.email,
                        first_name: data.first_name,
                        last_name: data.last_name
                    }
                };
            }

            // Account is complete, proceed with normal login
            setToken(data.access_token);
            setUser(data.user);

            localStorage.setItem('auth_token', data.access_token);
            localStorage.setItem('user_data', JSON.stringify(data.user));

            // Refresh user profile to get complete data including avatar
            try {
                const profileResponse = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                    headers: {
                        'Authorization': `Bearer ${data.access_token}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (profileResponse.ok) {
                    const freshUserData = await profileResponse.json();
                    setUser(freshUserData);
                    localStorage.setItem('user_data', JSON.stringify(freshUserData));
                }
            } catch (profileError) {
                console.warn('Failed to refresh profile after login:', profileError);
            }

            return { requiresSetup: false };
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (email: string, password: string, firstName: string, lastName: string, phone?: string, universityId?: string, instagram?: string, university?: string): Promise<{ success: boolean; message: string }> => {
        try {
            setIsLoading(true);
            const requestBody: any = {
                email,
                password,
                first_name: firstName,
                last_name: lastName
            };

            if (phone) {
                requestBody.phone = phone;
            }

            if (universityId) {
                const parsedId = parseInt(universityId, 10);
                const backendId = resolveBackendUniversityId(parsedId);
                if (backendId !== undefined) {
                    requestBody.university_id = backendId;
                }
            }

            if (university) {
                requestBody.university = university;
            }

            if (instagram) {
                requestBody.instagram = instagram;
            }

            const response = await fetch(API_ENDPOINTS.AUTH.REGISTER, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(formatApiErrorDetail(error.detail, 'Registration failed'));
            }

            const data = await response.json();

            // Register now returns AuthResponse, so automatically log the user in
            if (data.access_token && data.user) {
                setToken(data.access_token);
                setUser(data.user);
                localStorage.setItem('auth_token', data.access_token);
                localStorage.setItem('user_data', JSON.stringify(data.user));

                // Refresh user profile to get complete data including avatar
                try {
                    const profileResponse = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                        headers: {
                            'Authorization': `Bearer ${data.access_token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    if (profileResponse.ok) {
                        const freshUserData = await profileResponse.json();
                        setUser(freshUserData);
                        localStorage.setItem('user_data', JSON.stringify(freshUserData));
                    }
                } catch (profileError) {
                    console.warn('Failed to refresh profile after registration:', profileError);
                }
            }

            return { success: true, message: 'Registration successful!' };
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailVerificationSuccess = async (userData: any, token: string) => {
        setToken(token);
        setUser(userData);
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_data', JSON.stringify(userData));

        // Refresh user profile to get complete data including avatar
        try {
            const profileResponse = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (profileResponse.ok) {
                const freshUserData = await profileResponse.json();
                setUser(freshUserData);
                localStorage.setItem('user_data', JSON.stringify(freshUserData));
            }
        } catch (profileError) {
            console.warn('Failed to refresh profile after verification:', profileError);
        }
    };

    const registerWithGoogle = async (googleToken: string): Promise<{ requiresSetup?: boolean; setupData?: any }> => {
        try {
            setIsLoading(true);
            const response = await fetch(API_ENDPOINTS.AUTH.GOOGLE_LOGIN, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: googleToken }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(formatApiErrorDetail(error.detail, 'Google registration failed'));
            }

            const data = await response.json();

            // Check if account setup is required
            if (data.requires_setup) {
                return {
                    requiresSetup: true,
                    setupData: {
                        user_id: data.user_id,
                        email: data.email,
                        first_name: data.first_name,
                        last_name: data.last_name
                    }
                };
            }

            // Account is complete, proceed with normal login
            setToken(data.access_token);
            setUser(data.user);

            localStorage.setItem('auth_token', data.access_token);
            localStorage.setItem('user_data', JSON.stringify(data.user));

            // Refresh user profile to get complete data including avatar
            try {
                const profileResponse = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                    headers: {
                        'Authorization': `Bearer ${data.access_token}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (profileResponse.ok) {
                    const freshUserData = await profileResponse.json();
                    setUser(freshUserData);
                    localStorage.setItem('user_data', JSON.stringify(freshUserData));
                }
            } catch (profileError) {
                console.warn('Failed to refresh profile after login:', profileError);
            }

            return { requiresSetup: false };
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const registerWithMicrosoft = async (microsoftToken: string): Promise<{ requiresSetup?: boolean; setupData?: any }> => {
        try {
            setIsLoading(true);
            const response = await fetch(API_ENDPOINTS.AUTH.MICROSOFT_LOGIN, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: microsoftToken }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(formatApiErrorDetail(error.detail, 'Microsoft registration failed'));
            }

            const data = await response.json();

            // Check if account setup is required
            if (data.requires_setup) {
                return {
                    requiresSetup: true,
                    setupData: {
                        user_id: data.user_id,
                        email: data.email,
                        first_name: data.first_name,
                        last_name: data.last_name
                    }
                };
            }

            // Account is complete, proceed with normal login
            setToken(data.access_token);
            setUser(data.user);

            localStorage.setItem('auth_token', data.access_token);
            localStorage.setItem('user_data', JSON.stringify(data.user));

            // Refresh user profile to get complete data including avatar
            try {
                const profileResponse = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                    headers: {
                        'Authorization': `Bearer ${data.access_token}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (profileResponse.ok) {
                    const freshUserData = await profileResponse.json();
                    setUser(freshUserData);
                    localStorage.setItem('user_data', JSON.stringify(freshUserData));
                }
            } catch (profileError) {
                console.warn('Failed to refresh profile after login:', profileError);
            }

            return { requiresSetup: false };
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };


    const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...options.headers as Record<string, string>,
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        let response = await fetch(url, {
            ...options,
            headers,
        });

        // If we get a 401, try to refresh the token once
        if (response.status === 401 && token) {
            const refreshSuccess = await refreshToken();
            if (refreshSuccess) {
                // Retry the request with the new token
                const newToken = localStorage.getItem('auth_token');
                headers['Authorization'] = `Bearer ${newToken}`;
                response = await fetch(url, {
                    ...options,
                    headers,
                });
            } else {
                // Refresh failed, logout user
                logout();
            }
        }

        return response;
    };

    const updateProfile = async (updates: Partial<User>): Promise<void> => {
        if (!token) throw new Error('Not authenticated');

        try {
            const response = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updates),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(formatApiErrorDetail(error.detail, 'Profile update failed'));
            }

            const updatedUser = await response.json();
            setUser(updatedUser);
            localStorage.setItem('user_data', JSON.stringify(updatedUser));
        } catch (error) {
            throw error;
        }
    };

    const refreshUserProfile = async (): Promise<void> => {
        if (!token) throw new Error('Not authenticated');

        try {
            const response = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch user profile');
            }

            const freshUserData = await response.json();
            console.log('AuthContext: Refreshed user data:', freshUserData);
            console.log('AuthContext: Avatar URL from server:', freshUserData.avatar_url);
            setUser(freshUserData);
            localStorage.setItem('user_data', JSON.stringify(freshUserData));
        } catch (error) {
            throw error;
        }
    };

    const updatePreferences = async (preferences: { preferred_university_id?: number; preferred_term?: string }): Promise<void> => {
        if (!token) throw new Error('Not authenticated');

        try {
            const response = await fetch(API_ENDPOINTS.USERS.PREFERENCES, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(preferences),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(formatApiErrorDetail(error.detail, 'Preferences update failed'));
            }

            const result = await response.json();

            // Update the user object with new preferences
            if (user) {
                const updatedUser = {
                    ...user,
                    preferred_university_id: result.data.preferred_university_id,
                    preferred_term: result.data.preferred_term
                };
                setUser(updatedUser);
                localStorage.setItem('user_data', JSON.stringify(updatedUser));
            }
        } catch (error) {
            throw error;
        }
    };

    const loadUserPreferences = async (): Promise<void> => {
        if (!token) {
            throw new Error('Not authenticated');
        }

        if (!user) {
            throw new Error('User data not available');
        }

        try {
            const response = await fetch(API_ENDPOINTS.USERS.PREFERENCES, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to load user preferences: ${response.status} ${errorText}`);
            }

            const result = await response.json();

            // Update the user object with loaded preferences
            if (user) {
                const updatedUser = {
                    ...user,
                    preferred_university_id: result.data.preferred_university_id,
                    preferred_term: result.data.preferred_term
                };
                setUser(updatedUser);
                localStorage.setItem('user_data', JSON.stringify(updatedUser));
            }
        } catch (error) {
            throw error;
        }
    };

    const showGoogleSetupModal = (setupData: any) => {
        // TODO: Implement Google setup modal if needed
        console.log('Google setup required:', setupData);
    };

    const value: AuthContextType = {
        user,
        token,
        isLoading,
        isAuthenticated,
        isEmailVerified,
        login,
        loginWithGoogle,
        loginWithMicrosoft,
        register,
        registerWithGoogle,
        registerWithMicrosoft,
        logout,
        updateProfile,
        refreshUserProfile,
        updatePreferences,
        loadUserPreferences,
        handleEmailVerificationSuccess,
        refreshToken,
        makeAuthenticatedRequest,
        showGoogleSetupModal,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
